import React from 'react';
import { Plus, BarChart3, Target, CreditCard, Settings, FileText, ArrowUpDown, PiggyBank } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  href?: string;
  onClick?: () => void;
  color: string;
  bgColor: string;
  isPrimary?: boolean;
}

interface QuickActionsWidgetProps {
  safeT: (key: string) => string;
}

export const QuickActionsWidget: React.FC<QuickActionsWidgetProps> = ({ safeT }) => {
  const navigate = useNavigate();

  const quickActions: QuickAction[] = [
    {
      id: 'add-transaction',
      title: safeT('dashboard.quickActions.addTransaction') || 'Add Transaction',
      description: safeT('dashboard.quickActions.addTransactionDesc') || 'Record a new income or expense',
      icon: <Plus className="h-5 w-5" />,
      onClick: () => navigate('/app/transactions?add=true'),
      color: '#439476',
      bgColor: 'bg-[#439476]/10 dark:bg-[#439476]/20',
      isPrimary: true
    },
    {
      id: 'view-analytics',
      title: safeT('dashboard.quickActions.viewAnalytics') || 'View Analytics',
      description: safeT('dashboard.quickActions.viewAnalyticsDesc') || 'Analyze your spending patterns',
      icon: <BarChart3 className="h-5 w-5" />,
      href: '/app/analytics',
      color: '#2196F3',
      bgColor: 'bg-[#2196F3]/10 dark:bg-[#2196F3]/20'
    },
    {
      id: 'manage-budgets',
      title: safeT('dashboard.quickActions.manageBudgets') || 'Manage Budgets',
      description: safeT('dashboard.quickActions.manageBudgetsDesc') || 'Set and track your spending goals',
      icon: <Target className="h-5 w-5" />,
      href: '/app/budget',
      color: '#9C27B0',
      bgColor: 'bg-[#9C27B0]/10 dark:bg-[#9C27B0]/20'
    },
    {
      id: 'manage-accounts',
      title: safeT('dashboard.quickActions.manageAccounts') || 'Manage Accounts',
      description: safeT('dashboard.quickActions.manageAccountsDesc') || 'Add or edit your bank accounts',
      icon: <CreditCard className="h-5 w-5" />,
      href: '/app/accounts',
      color: '#FF9800',
      bgColor: 'bg-[#FF9800]/10 dark:bg-[#FF9800]/20'
    },
    {
      id: 'savings-goals',
      title: safeT('dashboard.quickActions.savingsGoals') || 'Savings Goals',
      description: safeT('dashboard.quickActions.savingsGoalsDesc') || 'Track your savings progress',
      icon: <PiggyBank className="h-5 w-5" />,
      href: '/app/saving-goals',
      color: '#4CAF50',
      bgColor: 'bg-[#4CAF50]/10 dark:bg-[#4CAF50]/20'
    },
    {
      id: 'reports',
      title: safeT('dashboard.quickActions.reports') || 'Generate Reports',
      description: safeT('dashboard.quickActions.reportsDesc') || 'Create detailed financial reports',
      icon: <FileText className="h-5 w-5" />,
      href: '/app/reports',
      color: '#607D8B',
      bgColor: 'bg-[#607D8B]/10 dark:bg-[#607D8B]/20'
    },
    {
      id: 'transfers',
      title: safeT('dashboard.quickActions.transfers') || 'Transfer Money',
      description: safeT('dashboard.quickActions.transfersDesc') || 'Move money between accounts',
      icon: <ArrowUpDown className="h-5 w-5" />,
      onClick: () => navigate('/app/transactions?add=true&type=transfer'),
      color: '#795548',
      bgColor: 'bg-[#795548]/10 dark:bg-[#795548]/20'
    },
    {
      id: 'settings',
      title: safeT('dashboard.quickActions.settings') || 'Settings',
      description: safeT('dashboard.quickActions.settingsDesc') || 'Customize your preferences',
      icon: <Settings className="h-5 w-5" />,
      href: '/app/settings',
      color: '#78909C',
      bgColor: 'bg-[#78909C]/10 dark:bg-[#78909C]/20'
    }
  ];

  const handleActionClick = (action: QuickAction) => {
    if (action.onClick) {
      action.onClick();
    }
  };

  const renderAction = (action: QuickAction) => {
    const content = (
      <div className={cn(
        "group relative p-4 rounded-xl border border-border dark:border-border-dark transition-all duration-200 hover:shadow-md hover:border-transparent hover:-translate-y-0.5",
        action.bgColor,
        action.isPrimary 
          ? "bg-gradient-to-br from-primary/5 to-secondary/5 dark:from-primary-dark/5 dark:to-secondary-dark/5"
          : "bg-background-secondary/30 dark:bg-background-dark-secondary/30 hover:bg-background-secondary dark:hover:bg-background-dark-secondary"
      )}>
        {/* Primary Action Indicator */}
        {action.isPrimary && (
          <div className="absolute top-2 right-2">
            <div className="w-2 h-2 bg-primary dark:bg-primary-dark rounded-full animate-pulse"></div>
          </div>
        )}

        <div className="flex items-start space-x-3 rtl:space-x-reverse">
          {/* Icon */}
          <div className={cn(
            "flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 transition-transform group-hover:scale-110",
            action.bgColor
          )}>
            <div style={{ color: action.color }}>
              {action.icon}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary mb-1 group-hover:text-primary dark:group-hover:text-primary-dark transition-colors">
              {action.title}
            </h4>
            <p className="text-xs text-text-secondary dark:text-text-dark-secondary leading-relaxed">
              {action.description}
            </p>
          </div>
        </div>

        {/* Hover Effect Border */}
        <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-primary/20 dark:group-hover:border-primary-dark/20 transition-all duration-200"></div>
      </div>
    );

    if (action.href) {
      return (
        <Link key={action.id} to={action.href} className="block">
          {content}
        </Link>
      );
    }

    return (
      <button
        key={action.id}
        onClick={() => handleActionClick(action)}
        className="block w-full text-left"
      >
        {content}
      </button>
    );
  };

  return (
    <div className="bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center justify-between p-6 border-b border-border dark:border-border-dark">
        <div className="flex items-center">
          <div className="bg-gradient-to-r from-secondary to-primary p-2 rounded-lg mr-3 rtl:ml-3 rtl:mr-0">
            <Plus className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              {safeT('dashboard.quickActions.title') || 'Quick Actions'}
            </h3>
            <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
              {safeT('dashboard.quickActions.subtitle') || 'Common tasks and shortcuts'}
            </p>
          </div>
        </div>
        <div className="text-xs px-2 py-1 rounded-full bg-gradient-to-r from-secondary/10 to-primary/10 text-secondary dark:text-primary-dark border border-secondary/20 dark:border-primary-dark/20">
          {quickActions.length} {safeT('dashboard.quickActions.actionsAvailable') || 'actions'}
        </div>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map(renderAction)}
        </div>

        {/* Featured Action Banner */}
        <div className="mt-6 p-4 bg-gradient-to-r from-primary/5 to-secondary/5 dark:from-primary-dark/5 dark:to-secondary-dark/5 border border-primary/10 dark:border-primary-dark/10 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <div className="bg-gradient-to-r from-primary to-secondary p-2 rounded-lg">
                <BarChart3 className="h-4 w-4 text-white" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                  {safeT('dashboard.quickActions.featured') || 'Try Analytics Dashboard'}
                </h4>
                <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
                  {safeT('dashboard.quickActions.featuredDesc') || 'Get deeper insights into your spending patterns'}
                </p>
              </div>
            </div>
            <Link 
              to="/app/analytics"
              className="text-xs px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 transition-colors font-medium"
            >
              {safeT('dashboard.quickActions.tryNow') || 'Try Now'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}; 